import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Clock } from "lucide-react";
import { Seo } from "../components/Seo";
import { Reveal } from "../components/Reveal";
import { getPosts } from "../lib/api";

const jsonLd = {
  "@context": "https://schema.org",
  "@type": "Blog",
  name: "OneCare Pharmacy Blog",
  url: "https://onecarelife.health/blog",
};

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    getPosts()
      .then(setPosts)
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  return (
    <main className="bg-[#F8FAFC]">
      <Seo
        title="OneCare Pharmacy Blog | Health, Service & Product Information"
        description="Guides and updates from the OneCare Pharmacy team: how our enquiry process works, pack sizes and prices explained, and responsible online pharmacy practice."
        path="/blog"
        jsonLd={jsonLd}
      />

      <section className="bg-white pb-12 pt-16 sm:pt-20">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          <p className="font-mono text-xs font-semibold tracking-[0.22em] text-emerald-700">BLOG</p>
          <h1 className="mt-3 max-w-3xl font-display text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            Guides & updates from our team
          </h1>
          <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Clear, practical articles about our service, product information and responsible healthcare — written to
            help you enquire with confidence.
          </p>
        </div>
      </section>

      <section className="py-16">
        <div className="mx-auto max-w-7xl px-5 lg:px-8">
          {posts.length > 0 ? (
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {posts.map((p, i) => (
                <Reveal key={p.slug} delay={i * 0.08}>
                  <article
                    data-testid={`blog-post-card-${p.slug}`}
                    className="flex h-full flex-col rounded-2xl border border-slate-200 bg-white p-7 transition-[transform,box-shadow,border-color] duration-300 hover:-translate-y-1 hover:border-emerald-600/30 hover:shadow-xl hover:shadow-emerald-900/10"
                  >
                    <div className="flex items-center gap-3">
                      <span className="rounded-full bg-[#D8EFE6] px-3 py-1 font-mono text-[11px] font-medium text-emerald-800">
                        {p.category}
                      </span>
                      <span className="font-mono text-[11px] text-slate-400">{p.date}</span>
                    </div>
                    <h2 className="mt-4 font-display text-xl font-bold leading-snug text-slate-900">{p.title}</h2>
                    <p className="mt-3 flex-1 text-sm leading-relaxed text-slate-600">{p.excerpt}</p>
                    <div className="mt-6 flex items-center justify-between">
                      <span className="flex items-center gap-1.5 font-mono text-xs text-slate-400">
                        <Clock className="h-3.5 w-3.5" />
                        {p.read_time}
                      </span>
                      <Link
                        to={`/blog/${p.slug}`}
                        data-testid={`blog-read-more-${p.slug}`}
                        className="group flex items-center gap-1.5 font-display text-sm font-bold text-emerald-800 transition-[color] hover:text-emerald-600"
                      >
                        Read article
                        <ArrowRight className="h-4 w-4 transition-[transform] group-hover:translate-x-1" />
                      </Link>
                    </div>
                  </article>
                </Reveal>
              ))}
            </div>
          ) : (
            <p data-testid="blog-empty-state" className="py-10 text-center text-sm text-slate-500">
              {loaded ? "New articles are on the way. Check back soon." : "Loading articles..."}
            </p>
          )}
        </div>
      </section>
    </main>
  );
}
